import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useDocuments } from "@/hooks/use-documents";
import { useDocumentFolders, useFolderTree, useDocumentDragDrop } from "@/hooks/use-document-folders";
import {
    FileText, ChevronDown, ChevronRight, Upload, Loader2, FolderPlus,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { FolderTreeContext, FolderNode, DocRow, NewFolderInput, DocumentViewerModal } from "./documents";
import type { FolderTreeContextValue, FolderDocument } from "./documents";
import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { useToast } from "@/hooks/use-toast";

interface DocumentsModuleProps {
    claimId: number;
    isOpen: boolean;
    onToggle: () => void;
    canWrite: boolean;
}

// Documents accepts PDFs, Office files, and text. Images/videos belong to the Photos module.
const DOCUMENT_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.txt";
const PHOTOS_SUPPORTED_MIME_TYPES = new Set([
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/bmp",
    "image/avif",
    "video/mp4",
    "video/webm",
]);

/** True for image/video files, which are not accepted by Documents. */
function isMediaFile(file: File): boolean {
    if (file.type.startsWith("image/") || file.type.startsWith("video/")) return true;
    return /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?|svg|mp4|mov|webm|avi|mkv)$/i.test(file.name);
}

function isPhotosSupportedFile(file: File): boolean {
    return PHOTOS_SUPPORTED_MIME_TYPES.has(file.type);
}

export function DocumentsModule({ claimId, isOpen, onToggle, canWrite }: DocumentsModuleProps) {
    const { toast } = useToast();
    const { documents, isLoading, uploadDocumentAsync, isUploading, getDocumentBlob, downloadDocument, deleteDocument, isDeleting } = useDocuments(claimId);
    const {
        folders, isLoading: foldersLoading, error: foldersError,
        createFolder, deleteFolder, updateFolder, moveDocument,
        isCreating: isCreatingFolder,
    } = useDocumentFolders(claimId);

    const tree = useFolderTree();

    const handleMoveFolder = useCallback(async (params: { folderId: number; parentId: number | null }) => {
        await updateFolder({ folderId: params.folderId, parentId: params.parentId });
        if (params.parentId !== null) tree.expandNode(params.parentId);
    }, [updateFolder, tree]);

    const drag = useDocumentDragDrop(moveDocument, handleMoveFolder);

    // ── Delete confirmation dialog state ──
    const [deleteTarget, setDeleteTarget] = useState<
        | { type: "folder"; id: number; name: string }
        | { type: "document"; id: number }
        | null
    >(null);

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteTarget) return;
        try {
            if (deleteTarget.type === "folder") {
                await deleteFolder(deleteTarget.id);
            } else {
                await deleteDocument(deleteTarget.id);
            }
        } catch {
            // Error toast shown by hook's onError callback
        } finally {
            setDeleteTarget(null);
        }
    }, [deleteTarget, deleteFolder, deleteDocument]);

    // ── Document viewer state ──
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerDoc, setViewerDoc] = useState<FolderDocument | null>(null);
    const [viewerBlobUrl, setViewerBlobUrl] = useState<string | null>(null);
    const [isViewerLoading, setIsViewerLoading] = useState(false);
    const [viewerError, setViewerError] = useState<string | null>(null);
    const viewerRequestRef = useRef(0);

    const clearViewerBlobUrl = useCallback(() => {
        setViewerBlobUrl(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
    }, []);

    const handleViewerOpenChange = useCallback((open: boolean) => {
        setViewerOpen(open);
        if (!open) {
            viewerRequestRef.current += 1;
            setViewerDoc(null);
            setViewerError(null);
            setIsViewerLoading(false);
            clearViewerBlobUrl();
        }
    }, [clearViewerBlobUrl]);

    const openDocumentViewer = useCallback(async (doc: FolderDocument) => {
        const requestId = viewerRequestRef.current + 1;
        viewerRequestRef.current = requestId;

        setViewerDoc(doc);
        setViewerOpen(true);
        setViewerError(null);
        setIsViewerLoading(true);
        clearViewerBlobUrl();

        try {
            const blob = await getDocumentBlob(doc.id);
            const nextBlobUrl = URL.createObjectURL(blob);

            // Ignore stale responses when a newer view request has started.
            if (viewerRequestRef.current !== requestId) {
                URL.revokeObjectURL(nextBlobUrl);
                return;
            }

            setViewerBlobUrl(nextBlobUrl);
        } catch {
            if (viewerRequestRef.current === requestId) {
                setViewerError("Failed to load document preview.");
            }
        } finally {
            if (viewerRequestRef.current === requestId) {
                setIsViewerLoading(false);
            }
        }
    }, [clearViewerBlobUrl, getDocumentBlob]);

    useEffect(() => {
        return () => {
            clearViewerBlobUrl();
        };
    }, [clearViewerBlobUrl]);

    const handleOpenViewerInNewTab = useCallback(() => {
        if (!viewerBlobUrl) return;
        window.open(viewerBlobUrl, "_blank", "noopener,noreferrer");
    }, [viewerBlobUrl]);

    const handleViewerDownload = useCallback(() => {
        if (!viewerDoc) return;
        void downloadDocument(viewerDoc.id, viewerDoc.filename);
    }, [downloadDocument, viewerDoc]);

    // ── File input refs ──
    const fileInputRef = useRef<HTMLInputElement>(null);

    // ── Upload to a specific folder ──
    const [uploadTargetFolderId, setUploadTargetFolderId] = useState<number | null>(null);

    const uploadFiles = useCallback(async (files: File[], folderId: number | null) => {
        const mediaFiles = files.filter(isMediaFile);
        const photosSupportedFiles = mediaFiles.filter(isPhotosSupportedFile);
        const unsupportedMediaFiles = mediaFiles.filter((file) => !isPhotosSupportedFile(file));
        const uploadable = files.filter((f) => !isMediaFile(f));
        if (photosSupportedFiles.length > 0) {
            toast({
                title: "Images and videos go in Photos",
                description: `Upload media in the Photos section instead: ${photosSupportedFiles.map((file) => file.name).join(", ")}`,
                variant: "destructive",
            });
        }
        if (unsupportedMediaFiles.length > 0) {
            toast({
                title: "Unsupported file type",
                description: `These files are not supported by Documents: ${unsupportedMediaFiles.map((file) => file.name).join(", ")}`,
                variant: "destructive",
            });
        }
        if (uploadable.length === 0) return;

        const failed: string[] = [];
        let succeeded = 0;
        for (const file of uploadable) {
            try {
                await uploadDocumentAsync({ file, ...(folderId != null ? { folderId } : {}) });
                succeeded += 1;
            } catch {
                failed.push(file.name);
            }
        }
        if (succeeded > 0 && folderId != null) tree.expandNode(folderId);
        if (failed.length > 0) {
            toast({
                title: failed.length === files.length ? "Upload failed" : "Some uploads failed",
                description: `Could not upload: ${failed.join(", ")}`,
                variant: "destructive",
            });
        }
    }, [uploadDocumentAsync, tree, toast]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!canWrite) return;
        const files = Array.from(event.target.files ?? []);
        if (files.length > 0) {
            void uploadFiles(files, uploadTargetFolderId);
        }
        event.target.value = "";
        setUploadTargetFolderId(null);
    };

    const handleTreeDrop = useCallback((targetFolderId: number | null) => (event: React.DragEvent) => {
        if (!canWrite) return;

        const droppedFiles = Array.from(event.dataTransfer.files ?? []);
        if (droppedFiles.length > 0) {
            event.preventDefault();
            event.stopPropagation();
            drag.handleDragEnd();
            void uploadFiles(droppedFiles, targetFolderId);
            return;
        }

        drag.handleDrop(targetFolderId)(event);
    }, [canWrite, drag, uploadFiles]);

    const handleUploadToFolder = useCallback((folderId: number) => {
        if (!canWrite) return;
        setUploadTargetFolderId(folderId);
        requestAnimationFrame(() => fileInputRef.current?.click());
    }, [canWrite]);

    const handleCreateFolder = useCallback(async (parentId: number | null) => {
        if (!canWrite) return;
        const name = tree.newName.trim();
        if (!name) return;
        try {
            await createFolder({ name, parentId });
            if (parentId !== null) tree.expandNode(parentId);
        } catch {
            // Error toast shown by hook's onError callback
        } finally {
            tree.cancelCreate();
        }
    }, [tree, createFolder, canWrite]);

    const handleRename = useCallback(async () => {
        if (!canWrite) return;
        if (!tree.renamingId || !tree.renameValue.trim()) {
            tree.cancelRename();
            return;
        }
        try {
            await updateFolder({ folderId: tree.renamingId, name: tree.renameValue.trim() });
        } catch {
            // Error toast shown by hook's onError callback
        } finally {
            tree.cancelRename();
        }
    }, [tree, updateFolder, canWrite]);

    // ── Pre-computed lookup maps (avoids O(n) filter in every FolderNode render) ──
    const childFoldersByParent = useMemo(() => {
        const map = new Map<number | null, typeof folders>();
        for (const f of folders) {
            const key = f.parentId ?? null;
            const list = map.get(key);
            if (list) list.push(f);
            else map.set(key, [f]);
        }
        return map;
    }, [folders]);

    const docsByFolder = useMemo(() => {
        const map = new Map<number | null, typeof documents>();
        for (const d of documents) {
            const key = (d.folderId as number | null | undefined) ?? null;
            const list = map.get(key);
            if (list) list.push(d);
            else map.set(key, [d]);
        }
        return map;
    }, [documents]);

    // ── Context value (memoized) ──
    const ctxValue = useMemo<FolderTreeContextValue>(() => ({
        canWrite,
        allFolders: folders,
        allDocuments: documents,
        childFoldersByParent,
        docsByFolder,
        expandedIds: tree.expandedIds,
        toggleExpanded: tree.toggleExpanded,
        renamingId: tree.renamingId,
        renameValue: tree.renameValue,
        setRenameValue: tree.setRenameValue,
        onRenameSubmit: handleRename,
        onRenameCancel: tree.cancelRename,
        onStartRename: tree.startRename,
        creatingInParent: tree.creatingInParent,
        newName: tree.newName,
        setNewName: tree.setNewName,
        onCreateSubmit: handleCreateFolder,
        onCreateCancel: tree.cancelCreate,
        onStartCreate: tree.startCreate,
        onDeleteFolder: (id, name) => {
            if (!canWrite) return;
            setDeleteTarget({ type: "folder", id, name });
        },
        isCreatingFolder,
        onOpenDoc: openDocumentViewer,
        onDownload: downloadDocument,
        onDeleteDoc: (id) => { if (canWrite) setDeleteTarget({ type: "document", id }); },
        isDeleting,
        draggingDocId: canWrite ? drag.draggingDocId : null,
        draggingFolderId: canWrite ? drag.draggingFolderId : null,
        dropTargetId: canWrite ? drag.dropTargetId : null,
        handleDragStart: canWrite ? drag.handleDragStart : () => () => {},
        handleFolderDragStart: canWrite ? drag.handleFolderDragStart : () => () => {},
        handleDragEnd: canWrite ? drag.handleDragEnd : () => {},
        handleDrop: canWrite ? handleTreeDrop : () => () => {},
        onDragOver: canWrite ? drag.onDragOver : () => () => {},
        onDragLeave: canWrite ? drag.onDragLeave : () => {},
        onUploadToFolder: handleUploadToFolder,
    }), [
        canWrite,
        folders, documents, childFoldersByParent, docsByFolder,
        isDeleting, isCreatingFolder,
        tree.expandedIds, tree.renamingId, tree.renameValue,
        tree.creatingInParent, tree.newName,
        tree.toggleExpanded, tree.setRenameValue, tree.cancelRename,
        tree.startRename, tree.setNewName, tree.cancelCreate, tree.startCreate,
        drag, handleCreateFolder, handleRename,
        openDocumentViewer, downloadDocument, handleUploadToFolder,
        handleTreeDrop,
    ]);

    const rootFolders = childFoldersByParent.get(null) ?? [];
    const rootDocs = docsByFolder.get(null) ?? [];
    const hasContent = rootFolders.length > 0 || rootDocs.length > 0 || documents.length > 0;

    return (
        <Card className="shadow-xs w-full overflow-hidden">
            <Collapsible open={isOpen} onOpenChange={onToggle}>
                <CollapsibleTrigger className="w-full">
                    <CardHeader className="py-3 px-4 hover:bg-muted/50 cursor-pointer transition-colors overflow-hidden">
                        <div className="flex items-center justify-between min-w-0 w-full">
                            <div className="flex items-center gap-2 min-w-0">
                                <FileText className="h-4 w-4 shrink-0" />
                                <CardTitle className="text-sm font-medium truncate">Documents</CardTitle>
                                <Badge variant="secondary" className="text-xs h-5 px-1.5 min-w-[1.25rem] shrink-0 flex items-center justify-center">
                                    {documents?.length || 0}
                                </Badge>
                            </div>
                            {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                        </div>
                    </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <CardContent className="px-4 pb-4 pt-0 w-full overflow-hidden">
                        <div className="pb-2 border-b mb-1"></div>
                        {/* ── Content ── */}
                        {(isLoading || foldersLoading) ? (
                            <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
                        ) : foldersError ? (
                            <p className="text-center py-6 text-xs text-destructive">Failed to load folders</p>
                        ) : (
                            <FolderTreeContext.Provider value={ctxValue}>
                                <div
                                    className="pt-0.5 px-0.5 max-h-[400px] overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-muted-foreground/20 hover:scrollbar-thumb-muted-foreground/40 w-full min-w-0"
                                    onDragOver={canWrite ? drag.onDragOver("root") : undefined}
                                    onDragLeave={canWrite ? drag.onDragLeave : undefined}
                                    onDrop={canWrite ? handleTreeDrop(null) : undefined}
                                >
                                    {/* ── New-folder input at root ── */}
                                    {tree.creatingInParent === "root" && (
                                        <NewFolderInput
                                            value={tree.newName}
                                            onChange={tree.setNewName}
                                            onSubmit={() => handleCreateFolder(null)}
                                            onCancel={tree.cancelCreate}
                                            depth={0}
                                            isCreating={isCreatingFolder}
                                        />
                                    )}

                                    {/* ── Tree: root folders ── */}
                                    {rootFolders.map(folder => (
                                        <FolderNode key={folder.id} folder={folder} depth={0} />
                                    ))}

                                    {/* ── Root-level documents ── */}
                                    {rootDocs.map(doc => (
                                        <DocRow key={doc.id} doc={doc} depth={0} />
                                    ))}

                                    {/* ── Empty state ── */}
                                    {!hasContent && (
                                        <p className="text-center py-6 text-xs text-muted-foreground">No documents yet</p>
                                    )}
                                </div>


                                {/* ── Drag hint ── */}
                                {hasContent && (
                                    <p className="text-center text-[10px] text-muted-foreground/50 pt-1.5 select-none">
                                        Drag documents or folders to rearrange, or drop files to upload
                                    </p>
                                )}
                            </FolderTreeContext.Provider>
                        )}

                        {/* ── Toolbar ── */}
                        <div className="pt-3 border-t mt-3 w-full flex items-center gap-1.5">
                            <input type="file" ref={fileInputRef} className="hidden" accept={DOCUMENT_ACCEPT} onChange={handleFileChange} disabled={isUploading || !canWrite} data-testid="root-upload-input" multiple />
                            <Button
                                size="sm"
                                className="flex-1 h-7 text-xs gap-1.5"
                                onClick={() => {
                                    if (!canWrite) return;
                                    // Clear any stale folder target from a cancelled folder-upload picker
                                    setUploadTargetFolderId(null);
                                    fileInputRef.current?.click();
                                }}
                                disabled={isUploading || !canWrite}
                            >
                                {isUploading
                                    ? <><Loader2 className="h-3 w-3 animate-spin" />Uploading…</>
                                    : <><Upload className="h-3 w-3" />Upload</>
                                }
                            </Button>
                            <Button
                                size="icon"
                                variant="outline"
                                className="h-7 w-7 shrink-0"
                                title="New folder"
                                onClick={() => canWrite && tree.startCreate("root")}
                                disabled={!canWrite}
                            >
                                <FolderPlus className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    </CardContent>
                </CollapsibleContent>
            </Collapsible>

            <ConfirmDeleteDialog
                open={deleteTarget !== null}
                onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
                onConfirm={async () => { if (canWrite) await handleDeleteConfirm(); }}
                title={deleteTarget?.type === "folder" ? "Delete Folder" : "Delete Document"}
                description={
                    deleteTarget?.type === "folder"
                        ? `Delete "${deleteTarget.name}"? Documents inside will be moved to the root level.`
                        : "Are you sure you want to delete this document? This action cannot be undone."
                }
            />

            <DocumentViewerModal
                open={viewerOpen}
                onOpenChange={handleViewerOpenChange}
                filename={viewerDoc?.filename}
                mimeType={viewerDoc?.mimeType}
                blobUrl={viewerBlobUrl}
                isLoading={isViewerLoading}
                error={viewerError}
                onOpenInNewTab={handleOpenViewerInNewTab}
                onDownload={handleViewerDownload}
            />
        </Card>
    );
}
